import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { AuthApi, UsersApi } from '@/api/endpoints';
import { ApiError, registerAuthStoreAccessor } from '@/api/client';
import type { MeResponse, ThemeMode } from '@/api/types';

/**
 * `users.permissions` exposed by /auth/me may be named `perms` (current
 * backend shape) or `permissions` (future). Accept both.
 */
function readPermissions(me: MeResponse): string[] {
  if (Array.isArray(me.permissions)) return me.permissions;
  if (Array.isArray(me.perms)) return me.perms;
  return [];
}

/**
 * Permissions that grant access to the office dashboard. A user must have at
 * least ONE of these to enter (Direktive #3 from the master brief). Drivers
 * with only driver.read_own (and similar _own/_self scoped perms) are sent
 * to /no-office.
 *
 * Wichtig: aufgenommen werden NUR Office-exklusive Perms, die die Mobile-
 * Rollen (driver, workshop) NICHT besitzen. Generische Perms wie
 * `tenant.read`, `vehicle.read`, `damage.read`, `settings.read`,
 * `shuttle.read`, `ticket.read`, `workshop_task.read` sind ABSICHTLICH NICHT
 * Teil der Whitelist — sie sind auch an Mobile-Rollen vergeben (siehe
 * `backend/seeds/001_dev_seed.sql`) und würden die Sperre umgehen.
 */
const OFFICE_PERMISSIONS = [
  'driver.read',
  'driver.create',
  'driver.write',
  'driver.suspend',
  'driver.terminate',
  'driver.archive',
  'driver.export',
  'fleet.read',
  'vehicle.import',
  'vehicle.assign',
  'vehicle.unassign',
  'accommodation.read',
  'accommodation.write',
  'station.read',
  'station.write',
  'station.admin',
  'attendance.read',
  'attendance.write',
  'damage.resolve',
  'document.read',
  'fuel.read',
  'fuel.validate',
  'geotab.read',
  'geotab.alert.acknowledge',
  'onboarding.read',
  'onboarding.write',
  'payroll.read',
  'permission.read',
  'reporting.read',
  'reporting.export',
  'reporting.admin',
  'role.read',
  'role.assign',
  'sim.read',
  'sim.write',
  'audit.read',
  'audit.read_all',
  'user.read',
  'user.write',
  'tenant.write',
  'tenant.admin',
  'tenant.switch',
  'system.maintenance',
  'system.tenant.provision',
];

/**
 * Belt-and-suspenders: Mobile-only-Rollen werden zusätzlich role-basiert
 * ausgesperrt. Selbst wenn eine zukünftige Permission-Vergabe unbeabsichtigt
 * eine Office-Permission an einen Driver/Workshop hängt, blockt diese
 * zweite Schicht den Zutritt. Server bleibt autoritativ — beide Layer sind
 * UX, kein Sicherheits-Layer.
 */
const MOBILE_ONLY_ROLES = new Set(['driver', 'workshop']);

const LS_KEY_ACCESS = 'jatrendo.auth.access_token';
const LS_KEY_REFRESH = 'jatrendo.auth.refresh_token';
const LS_KEY_TENANT_SLUG = 'jatrendo.auth.last_tenant_slug';

export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref<string | null>(localStorage.getItem(LS_KEY_ACCESS));
  const refreshToken = ref<string | null>(localStorage.getItem(LS_KEY_REFRESH));
  const me = ref<MeResponse | null>(null);
  const permissions = ref<Set<string>>(new Set());
  const role = ref<string | null>(null);
  const loading = ref(false);
  const lastError = ref<string | null>(null);

  const isAuthenticated = computed(() => !!accessToken.value);
  const user = computed(() => me.value?.user ?? null);
  const tenant = computed(() => me.value?.tenant ?? null);
  const hasOfficeAccess = computed(() => {
    // Mobile-only-Rollen werden hart ausgesperrt (zweite Schicht zur
    // Permission-Whitelist).
    if (role.value && MOBILE_ONLY_ROLES.has(role.value)) return false;
    return OFFICE_PERMISSIONS.some((p) => permissions.value.has(p));
  });

  function persistTokens() {
    if (accessToken.value) localStorage.setItem(LS_KEY_ACCESS, accessToken.value);
    else localStorage.removeItem(LS_KEY_ACCESS);
    if (refreshToken.value) localStorage.setItem(LS_KEY_REFRESH, refreshToken.value);
    else localStorage.removeItem(LS_KEY_REFRESH);
  }

  function applyMe(payload: MeResponse) {
    me.value = payload;
    permissions.value = new Set(readPermissions(payload));
    role.value = payload.role;
  }

  async function login(email: string, password: string, tenantSlug?: string) {
    loading.value = true;
    lastError.value = null;
    try {
      const res = await AuthApi.login({ email, password, tenantSlug });
      accessToken.value = res.access_token;
      refreshToken.value = res.refresh_token;
      persistTokens();
      if (tenantSlug) localStorage.setItem(LS_KEY_TENANT_SLUG, tenantSlug);

      // Pull /me to get tenant + permissions + theme_preference
      const meRes = await AuthApi.me();
      applyMe(meRes);
      return meRes;
    } catch (err) {
      if (err instanceof ApiError) {
        lastError.value = err.problem.detail ?? err.problem.title ?? err.message;
      } else {
        lastError.value = (err as Error).message;
      }
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function loadMe(): Promise<MeResponse | null> {
    if (!accessToken.value) return null;
    try {
      const meRes = await AuthApi.me();
      applyMe(meRes);
      return meRes;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // refresh will be attempted by the client itself; if even that fails,
        // forceLogout is invoked. Nothing else to do here.
        return null;
      }
      throw err;
    }
  }

  async function refresh(): Promise<string | null> {
    if (!refreshToken.value) return null;
    try {
      const res = await AuthApi.refresh(refreshToken.value);
      accessToken.value = res.access_token;
      refreshToken.value = res.refresh_token;
      persistTokens();
      return accessToken.value;
    } catch {
      forceLogout('refresh_failed');
      return null;
    }
  }

  async function logout() {
    try {
      if (refreshToken.value) await AuthApi.logout(refreshToken.value);
    } catch {
      /* best-effort, ignore */
    }
    forceLogout('user_logout');
  }

  function forceLogout(reason = 'logout') {
    accessToken.value = null;
    refreshToken.value = null;
    me.value = null;
    permissions.value = new Set();
    role.value = null;
    lastError.value = null;
    localStorage.removeItem(LS_KEY_ACCESS);
    localStorage.removeItem(LS_KEY_REFRESH);
    if (reason !== 'user_logout') {
      console.warn('[auth] forced logout:', reason);
    }
  }

  /** Persist theme choice to backend in a fire-and-forget manner. */
  async function persistThemePreference(mode: ThemeMode): Promise<void> {
    if (!accessToken.value || !me.value) return;
    try {
      await UsersApi.updateMe({ theme_preference: mode });
      if (me.value) {
        me.value.user.theme_preference = mode;
      }
    } catch (err) {
      // The endpoint may not exist yet (parallel-built /users/me). Treat 404
      // and 501 as soft-failures — UI still works via localStorage cache.
      if (err instanceof ApiError) {
        if (err.status === 404 || err.status === 501) {
          console.warn('[theme] PATCH /users/me not available yet:', err.code);
          return;
        }
      }
      console.warn('[theme] failed to persist theme on server:', err);
    }
  }

  function hasPermission(perm: string | string[]): boolean {
    if (Array.isArray(perm)) return perm.some((p) => permissions.value.has(p));
    return permissions.value.has(perm);
  }

  // Register accessor so the API client can call us back for refresh/logout.
  registerAuthStoreAccessor(() => ({
    get accessToken() {
      return accessToken.value;
    },
    refresh,
    forceLogout,
  }));

  return {
    accessToken,
    refreshToken,
    me,
    user,
    tenant,
    role,
    permissions,
    isAuthenticated,
    hasOfficeAccess,
    loading,
    lastError,
    login,
    logout,
    forceLogout,
    refresh,
    loadMe,
    persistThemePreference,
    hasPermission,
  };
});
